import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/** Columns exposed through a public share link — never the owner or internals. */
const SHARED_QUOTE_COLUMNS =
  "id, client_name, client_company, client_email, plan_name, hours, work_level, hourly_rate, monthly_rate, hourly_cost, retainer_cost, retainer_blocks, use_retainer, estimate, savings, custom, note, created_at";

export const createQuoteShare = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { quoteId: string; expiresInDays?: number }) => {
    if (!/^[0-9a-f-]{36}$/i.test(input.quoteId)) throw new Error("Invalid quote id");
    const days = Math.min(90, Math.max(1, Math.round(input.expiresInDays ?? 14)));
    return { quoteId: input.quoteId, expiresInDays: days };
  })
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    const { data: roles } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "admin")
      .maybeSingle();
    if (!roles) throw new Error("Forbidden");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: quote } = await supabaseAdmin
      .from("saved_quotes")
      .select("id")
      .eq("id", data.quoteId)
      .maybeSingle();
    if (!quote) throw new Error("Quote not found");

    const bytes = new Uint8Array(24);
    crypto.getRandomValues(bytes);
    const token = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");

    const expiresAt = new Date(Date.now() + data.expiresInDays * 24 * 60 * 60 * 1000).toISOString();

    const { error } = await supabaseAdmin.from("quote_shares").insert({
      quote_id: data.quoteId,
      token,
      expires_at: expiresAt,
      created_by: userId,
    });
    if (error) throw error;

    return { token, expiresAt, path: `/quote/${token}` };
  });

export const revokeQuoteShare = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { shareId: string }) => {
    if (!/^[0-9a-f-]{36}$/i.test(input.shareId)) throw new Error("Invalid share id");
    return input;
  })
  .handler(async ({ data, context }) => {
    const { data: roles } = await context.supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", context.userId)
      .eq("role", "admin")
      .maybeSingle();
    if (!roles) throw new Error("Forbidden");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("quote_shares")
      .update({ revoked_at: new Date().toISOString() })
      .eq("id", data.shareId)
      .is("revoked_at", null);
    if (error) throw error;
    return { ok: true };
  });

/**
 * Public: resolves one share token to its single quote. Unknown, revoked or
 * expired tokens return `null` so no other quote is ever reachable.
 */
export const getSharedQuote = createServerFn({ method: "GET" })
  .inputValidator((input: { token: string }) => ({
    token: String(input?.token ?? "").toLowerCase(),
  }))
  .handler(async ({ data }) => {
    // Malformed tokens are simply "not found" — never a 500 for a shared link.
    if (!/^[0-9a-f]{16,96}$/.test(data.token)) return null;

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: share } = await supabaseAdmin
      .from("quote_shares")
      .select("id, quote_id, expires_at, revoked_at, view_count")
      .eq("token", data.token)
      .maybeSingle();

    if (!share || share.revoked_at) return null;
    if (new Date(share.expires_at).getTime() <= Date.now()) {
      return { expired: true as const };
    }

    const { data: quote } = await supabaseAdmin
      .from("saved_quotes")
      .select(SHARED_QUOTE_COLUMNS)
      .eq("id", share.quote_id)
      .maybeSingle();
    if (!quote) return null;

    await supabaseAdmin
      .from("quote_shares")
      .update({
        view_count: (share.view_count ?? 0) + 1,
        last_viewed_at: new Date().toISOString(),
      })
      .eq("id", share.id);

    return { expired: false as const, expiresAt: share.expires_at, quote };
  });
